import {
  CreateOptionalSubtenantDictionaryRecordDto,
  CreateDictionaryRecordDto,
  OptionalSubtenantDictionaryRecordDto,
  UpdateOptionalSubtenantDictionaryRecordDto,
  UpdateDictionaryRecordDto,
} from '@lmt/shared-types';
import {
  maxLengthRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import React, { FC, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useQueryClient } from '@tanstack/react-query';
import { DictionaryInfo } from '../dictionaries.types';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { HookFormDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { snakeCase } from 'lodash';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useTranslation } from 'react-i18next';

export interface CreateOptionalSubtenantDictionaryRecordProps {
  dictionary: DictionaryInfo;
  data?: OptionalSubtenantDictionaryRecordDto;
}

const nameof = nameofFactory<CreateOptionalSubtenantDictionaryRecordDto>();

export const CreateOrEditDictionaryRecord: FC<
  FormComponentProps & CreateOptionalSubtenantDictionaryRecordProps
> = (props) => {
  const { formType, formId, tryCompleteForm, changeFormTitle } = props;
  const i18next = useScopedTranslation('dictionaries.form', NS_DataBlockFields);
  const i18nextGlobal = useTranslation(NS_DataBlockFields);
  const queryClient = useQueryClient();

  useEffect(() => {
    const name = i18nextGlobal.t(
      `dictionaries.${snakeCase(props.dictionary.name)}.name` as any,
    );
    if (props.data) {
      changeFormTitle(formId, i18next.t('edit_form_title', { name: name }));
    } else {
      changeFormTitle(formId, i18next.t('create_form_title', { name: name }));
    }
  }, []);

  const subtenants = useSubtenants();

  const { register, handleSubmit, formState, setError, control } =
    useForm<CreateOptionalSubtenantDictionaryRecordDto>({
      shouldFocusError: true,
      criteriaMode: 'all',
      defaultValues: props.data && {
        subtenantId: props.data.subtenantId,
        value: props.data.value,
      },
    });

  const submitForm = useErrorHandler(
    useCallback(
      async (data: CreateOptionalSubtenantDictionaryRecordDto) => {
        try {
          await tryCompleteForm(
            formType,
            formId,
            async () => {
              if (props.data) {
                if (props.dictionary.isOptionalSubtenant) {
                  await props.dictionary.edit(props.data.id, data);
                } else {
                  const record: UpdateDictionaryRecordDto = {
                    value: data.value,
                  };
                  await props.dictionary.edit(props.data.id, record);
                }
              } else {
                if (props.dictionary.isOptionalSubtenant) {
                  await props.dictionary.create(data);
                } else {
                  const record: CreateDictionaryRecordDto = {
                    value: data.value,
                  };
                  await props.dictionary.create(record);
                }
              }
              if (props.dictionary.invalidate) {
                await props.dictionary.invalidate(queryClient);
              }
            },
            i18next.t('loading'),
          );
        } catch (error) {
          const errorDetails =
            handleSubmitFormError<CreateOptionalSubtenantDictionaryRecordDto>(
              error,
              setError,
            );
          if (errorDetails.overallServerError) {
            setError('value', {
              message: errorDetails.overallServerError,
              type: 'validate',
            });
          }
        }
      },
      [formType, formId, tryCompleteForm],
    ),
    setError,
  );

  return (
    <form
      className={styles.autoSizedContainer}
      onSubmit={handleSubmit(submitForm.handler)}
      data-test-id="dictionaryRecordFormPopup"
    >
      <div className={styles.dataContainer}>
        <div className={styles.firstRow}>
          <Field
            className={styles.field}
            title={i18next.t('value')}
            required={true}
          >
            <Input
              {...register(nameof('value'), {
                ...requiredRule(),
                ...(props.dictionary.maxSize
                  ? maxLengthRule(props.dictionary.maxSize)
                  : null),
              })}
              autoComplete="off"
              autoFocus={true}
              errorText={formState.errors.value?.message}
            />
          </Field>
        </div>
        {props.dictionary.isOptionalSubtenant && (
          <Field className={styles.field} title={i18next.t('subtenant')}>
            <HookFormDropDownInput
              control={control}
              name={'subtenantId'}
              required
              variant="formInput"
              getOptionLabel="name"
              idFunction="id"
              errorText={formState.errors.subtenantId?.message}
              options={subtenants}
              testId="dictionary-record-subtenantId"
            />
          </Field>
        )}
      </div>

      <div className={styles.submitContainer}>
        <Error error={submitForm.overallServerError} />
        <SubmitButton
          submitting={formState.isSubmitting}
          className={styles.submit}
          title={
            props.data ? i18next.t('edit_button') : i18next.t('create_button')
          }
        />
      </div>
    </form>
  );
};
